import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, AlertTriangle, Info, CheckCircle, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnnouncementBanner = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('dismissed_announcements') || '[]');
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const res = await axios.get(`${API}/announcements/active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAnnouncements(res.data);
      } catch (error) {
        console.log('Failed to fetch announcements');
      }
    };

    fetchAnnouncements();
    // Refresh every 5 minutes
    const interval = setInterval(fetchAnnouncements, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleDismiss = async (announcementId) => {
    const newDismissed = [...dismissedIds, announcementId];
    setDismissedIds(newDismissed);
    localStorage.setItem('dismissed_announcements', JSON.stringify(newDismissed));
    
    try {
      await axios.post(`${API}/announcements/${announcementId}/dismiss`);
    } catch (error) {
      // Silently fail - already stored locally
    }
  };

  const visibleAnnouncements = announcements.filter(a => !dismissedIds.includes(a.id));

  if (visibleAnnouncements.length === 0) return null;

  const getIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="w-5 h-5" />;
      case 'error': return <AlertCircle className="w-5 h-5" />;
      case 'success': return <CheckCircle className="w-5 h-5" />;
      default: return <Info className="w-5 h-5" />;
    }
  };

  const getStyles = (type) => {
    switch (type) {
      case 'warning': 
        return 'bg-gradient-to-r from-amber-500 to-orange-500 text-white';
      case 'error': 
        return 'bg-gradient-to-r from-red-500 to-rose-500 text-white';
      case 'success': 
        return 'bg-gradient-to-r from-green-500 to-emerald-500 text-white';
      default: 
        return 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white';
    }
  };

  return (
    <div className="space-y-0" data-testid="announcement-banner-container">
      {visibleAnnouncements.map(announcement => (
        <div 
          key={announcement.id}
          className={`${getStyles(announcement.type)} py-3 px-4`}
          data-testid={`announcement-${announcement.id}`}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getIcon(announcement.type)}
              <div>
                <span className="font-semibold">{announcement.title}</span>
                <span className="mx-2">—</span>
                <span className="opacity-90">{announcement.message}</span>
              </div>
            </div>
            {announcement.is_dismissible && (
              <button
                onClick={() => handleDismiss(announcement.id)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Dismiss announcement"
                data-testid={`dismiss-announcement-${announcement.id}`}
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AnnouncementBanner;
